import React, { useEffect } from 'react';
import { styled, keyframes } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Logo from 'components/atoms/Logo';
import colors from 'theme/emotion/colors';
import { ROUTES } from 'navigation/constants';
import { getRegistrationLoading, getRegistrationError } from '../../selectors';
import { getAccessToken } from 'features/Auth/selectors';

const Container = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(to top, ${colors.primary.dark}, ${colors.primary.main})`,
  padding: '2rem',
  zIndex: 9999, // Ensure it covers everything including header
});

const ContentWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '3rem',
  width: '100%',
  maxWidth: '400px',
});

const LogoWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '120px',
  height: '120px',
  backgroundColor: colors.neutral.white,
  borderRadius: '50%',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
});

const StyledLogo = styled(Logo)({
  width: '80px',
  height: '80px',
});

const LoaderWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1.5rem',
});

const blink = keyframes`
  0%, 60%, 100% {
    opacity: 0.3;
  }
  30% {
    opacity: 1;
  }
`;

const DotsContainer = styled('div')({
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'center',
  justifyContent: 'center',
});

const Dot = styled('div')<{ delay: number }>(({ delay }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: colors.neutral.white,
  animation: `${blink} 1.4s infinite both`,
  animationDelay: `${delay}s`,
}));

const LoadingText = styled('p')({
  fontSize: '1.125rem',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 500,
  color: colors.neutral.white,
  margin: 0,
  textAlign: 'center',
});

const ErrorText = styled('p')({
  fontSize: '0.875rem',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 400,
  color: colors.error.main,
  backgroundColor: colors.neutral.white,
  padding: '0.75rem 1.5rem',
  borderRadius: '0.5rem',
  margin: '1rem 0 0 0',
  textAlign: 'center',
  maxWidth: '20rem',
});

interface CreatingAccountProps {}

const CreatingAccount: React.FC<CreatingAccountProps> = () => {
  const navigate = useNavigate();
  const isLoading = useSelector(getRegistrationLoading);
  const error = useSelector(getRegistrationError);
  const accessToken = useSelector(getAccessToken);
  const { t } = useTranslation();

  useEffect(() => {
    // Navigate to lessons if registration is successful
    if (!isLoading && accessToken) {
      // Small delay to show the loading screen briefly
      const timer = setTimeout(() => {
        navigate(ROUTES.HOME.path);
      }, 500);
      return () => clearTimeout(timer);
    } else if (!isLoading && error) {
      // Navigate back to email entry if there's an error
      // Show error briefly then go back
      const timer = setTimeout(() => {
        navigate(ROUTES.ONBOARDING.EMAIL.path);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, accessToken, error, navigate]);

  return (
    <Container>
      <ContentWrapper>
        <LogoWrapper>
          <StyledLogo />
        </LogoWrapper>
        
        <LoaderWrapper>
          {!error && (
            <>
              <DotsContainer>
                <Dot delay={0} />
                <Dot delay={0.2} />
                <Dot delay={0.4} />
              </DotsContainer>
              <LoadingText>{t('onboarding.creatingAccount')}</LoadingText>
            </>
          )}
          {error && (
            <>
              <LoadingText>{t('onboarding.accountCreationFailed')}</LoadingText>
              <ErrorText>{error}</ErrorText>
            </>
          )}
        </LoaderWrapper>
      </ContentWrapper>
    </Container>
  );
};

export default CreatingAccount; 